import RandomAccessStorage from 'random-access-storage'
import b4a from 'b4a'

/**
 * Hypercore storage backed by IndexedDB.
 *
 * The published random-access-idb was written when everything in this stack
 * was a Node Buffer and calls `.copy()` on the data it is handed; b4a gives
 * browsers plain Uint8Arrays, so it throws on the first write. This is a
 * direct implementation against the same random-access-storage interface
 * Hypercore expects.
 *
 * Files are stored as fixed-size pages, so appending a block to a 200 MB core
 * rewrites one page rather than the whole file, and each file keeps a metadata
 * record holding its length — IndexedDB has no notion of a sparse file, and
 * Hypercore needs reads past the end to fail rather than return zeroes.
 *
 * Writes are serialised per file. random-access-storage classes read, write,
 * del, truncate and stat as non-blocking and dispatches them concurrently; two
 * writes landing in the same page would each read the old page and each write
 * it back, and whichever finished second would silently erase the other's
 * bytes. Hypercore only surfaces that much later, as a block that will not
 * decode, so the queue below is load-bearing rather than defensive.
 */

const PAGE_SIZE = 4096
const STORE = 'pine'

/*
 * The connection is not ours to keep.
 *
 * Safari on the iPhone drops it whenever the page has been in the background
 * for a while: the process that serves IndexedDB is suspended, and every
 * request on the old connection fails from then on — "Connection to Indexed
 * Database server lost. Refresh the page to try again", or InvalidStateError
 * on the next transaction. The page itself carries on, so a connection opened
 * once and held for the life of the page meant that after the first trip to
 * another app nothing could be written any more: not a message, not a photo,
 * not even the fact that somebody had just signed in. A fresh connection
 * works, so an operation that meets a dead one opens another and runs again.
 * Each operation is one transaction and each transaction is all-or-nothing,
 * so running it again cannot write anything twice.
 */
const RETRY_DELAYS = [0, 250, 1000, 3000]

/*
 * Fewer, bigger transactions.
 *
 * Every transaction is a round trip to the process that owns the database,
 * and in Safari that round trip is slow: about twenty milliseconds, against
 * one or two in Chrome. Sending one message touches the room's own log, the
 * system core, the view and their indexes — close to three hundred
 * transactions when each page was read and written on its own, which is
 * seven seconds on an iPhone during which the message had not gone. So pages
 * written in the same moment, from whichever file, share one transaction;
 * reads asked for together share another; and the pages most recently used
 * are kept in memory, so the page an append lands on usually need not be
 * read back first at all.
 */
const CACHE_PAGES = 2048 // 8 MB

/*
 * A write is done once the database has it, not once the disk has been told
 * to flush it. That is what the native apps get from the file system as well
 * — Hypercore on a phone or a desktop does not fsync every block — and the
 * difference is only felt if the machine itself loses power in the moment
 * after. Safari otherwise waits for the flush on every transaction, which is
 * most of what a write costs there. Browsers without the option ignore it.
 */
const DURABILITY = { durability: 'relaxed' }

const connections = new Map() // database name -> Promise<IDBDatabase>

/**
 * Open the database, creating our object store if it is missing.
 *
 * A database of this name may already exist at some version — an older build,
 * or a different storage library — so we cannot simply ask for version 1 and
 * assume onupgradeneeded fires. Open whatever is there, and only reopen a
 * version higher if our store is absent.
 */
function openDatabase (name) {
  const held = connections.get(name)
  if (held) return held

  const opening = (async () => {
    let db = await openRequest(indexedDB.open(name))
    if (!db.objectStoreNames.contains(STORE)) {
      const version = db.version + 1
      db.close()
      db = await openRequest(indexedDB.open(name, version), (upgrading) => {
        if (!upgrading.objectStoreNames.contains(STORE)) upgrading.createObjectStore(STORE)
      })
    }
    // Closed by the browser rather than by us: the next operation opens anew.
    db.onclose = () => forget(name, opening)
    // Another tab upgrading or deleting the database waits on every open
    // connection; stepping aside is what lets it through.
    db.onversionchange = () => {
      forget(name, opening)
      db.close()
    }
    return db
  })()

  connections.set(name, opening)
  opening.catch(() => forget(name, opening))
  return opening
}

function forget (name, opening) {
  if (connections.get(name) === opening) connections.delete(name)
}

function discard (name, opening) {
  forget(name, opening)
  opening.then((db) => { try { db.close() } catch {} }, noop)
}

function openRequest (req, onUpgrade) {
  return new Promise((resolve, reject) => {
    if (onUpgrade) req.onupgradeneeded = () => onUpgrade(req.result)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
    req.onblocked = () => reject(new Error('IndexedDB is blocked by another Pine tab — close it and reload'))
  })
}

/**
 * Run one transaction on a connection that is still alive.
 *
 * `fn` gets the object store and must issue every request before it awaits
 * anything else: a transaction goes inactive as soon as control returns to
 * the event loop with no request outstanding.
 */
async function transact (name, mode, fn) {
  let failure = null
  for (const delay of RETRY_DELAYS) {
    if (delay) await sleep(delay)
    const opening = openDatabase(name)
    try {
      const db = await opening
      const tx = db.transaction(STORE, mode, DURABILITY)
      const done = settled(tx)
      done.catch(noop) // awaited below; a failed request rejects it first
      const result = await fn(tx.objectStore(STORE))
      await done
      return result
    } catch (err) {
      if (!connectionLost(err)) throw err
      failure = err
      discard(name, opening)
    }
  }
  throw failure
}

/**
 * What a dead connection looks like. Nothing a retry could make worse is in
 * here: a full disk (QuotaExceededError) or a bad key (DataError) is not.
 */
function connectionLost (err) {
  if (!err) return false
  return err.name === 'InvalidStateError' ||
    err.name === 'UnknownError' ||
    err.name === 'AbortError' ||
    /connection/i.test(err.message || '')
}

/** One database, as every file of one storage sees it. */
class Database {
  constructor (name) {
    this.name = name
    this.pages = new Map() // key -> page bytes, least recently used first
    this.writing = null
    this.reading = null
  }

  cached (key) {
    const page = this.pages.get(key)
    if (page) {
      this.pages.delete(key)
      this.pages.set(key, page)
    }
    return page
  }

  remember (key, page) {
    this.pages.delete(key)
    this.pages.set(key, page)
    if (this.pages.size > CACHE_PAGES) this.pages.delete(this.pages.keys().next().value)
  }

  /** Values for these keys, read alongside whatever else was asked for in this tick. */
  get (keys) {
    if (!this.reading) {
      const batch = this.reading = { keys: [], ...deferred() }
      queueMicrotask(() => this._read(batch))
    }
    const batch = this.reading
    const start = batch.keys.length
    batch.keys.push(...keys)
    return batch.promise.then((values) => values.slice(start, start + keys.length))
  }

  async _read (batch) {
    if (this.reading === batch) this.reading = null
    try {
      batch.resolve(await transact(this.name, 'readonly', (store) =>
        Promise.all(batch.keys.map((key) => promisify(store.get(key))))))
    } catch (err) {
      batch.reject(err)
    }
  }

  /**
   * Puts and deletes, committed together with everything else written in
   * this moment. Settles once they are on disk; all or none of them are.
   */
  write (ops) {
    if (!this.writing) {
      const batch = this.writing = { ops: [], ...deferred() }
      setTimeout(() => this._write(batch))
    }
    this.writing.ops.push(...ops)
    return this.writing.promise
  }

  async _write (batch) {
    if (this.writing === batch) this.writing = null
    try {
      await transact(this.name, 'readwrite', (store) => {
        for (const [kind, key, value] of batch.ops) {
          if (kind === 'put') store.put(value, key)
          else store.delete(key)
        }
      })
      for (const [kind, key, value] of batch.ops) {
        if (kind === 'put' && value instanceof Uint8Array) this.remember(key, value)
        else this.pages.delete(key)
      }
      batch.resolve()
    } catch (err) {
      for (const [, key] of batch.ops) this.pages.delete(key)
      batch.reject(err)
    }
  }
}

class IdbFile extends RandomAccessStorage {
  constructor (db, file) {
    super()
    this.db = db
    this.file = file
    this.length = 0

    // Tail of the per-file mutation queue.
    this._tail = Promise.resolve()
  }

  get _metaKey () {
    return this.file + '!meta'
  }

  _pageKey (index) {
    return this.file + '!' + index
  }

  /** Run `fn` after every mutation queued before it, whatever their outcome. */
  _serial (fn) {
    const next = this._tail.then(fn, fn)
    this._tail = next.then(noop, noop)
    return next
  }

  async _open (req) {
    try {
      const meta = await this._get(this._metaKey)
      this.length = meta && typeof meta.size === 'number' ? meta.size : 0
      req.callback(null)
    } catch (err) {
      req.callback(err)
    }
  }

  _write (req) {
    this._serial(() => this._writeSerial(req))
  }

  /**
   * Partial pages are read in one transaction, awaited, and written back in a
   * second — nothing may await between requests on one transaction.
   *
   * The length moves only once the bytes are in. A read of the new range
   * before then fails as past the end, instead of finding pages that are not
   * there yet and returning zeroes for them.
   */
  async _writeSerial (req) {
    try {
      const data = b4a.from(req.data)
      const end = req.offset + data.byteLength

      if (data.byteLength === 0) {
        if (end > this.length) await this._setLength(end)
        return req.callback(null)
      }

      const firstPage = Math.floor(req.offset / PAGE_SIZE)
      const lastPage = Math.floor((end - 1) / PAGE_SIZE)

      const partial = []
      for (let page = firstPage; page <= lastPage; page++) {
        const pageStart = page * PAGE_SIZE
        const writeStart = Math.max(req.offset, pageStart)
        const writeEnd = Math.min(end, pageStart + PAGE_SIZE)
        if (writeStart !== pageStart || writeEnd !== pageStart + PAGE_SIZE) partial.push(page)
      }

      // Only pages inside the file have anything to keep.
      const existing = await this._pages(partial.filter((page) => page * PAGE_SIZE < this.length))

      const length = Math.max(this.length, end)
      const ops = []
      for (let page = firstPage; page <= lastPage; page++) {
        const pageStart = page * PAGE_SIZE
        const writeStart = Math.max(req.offset, pageStart)
        const writeEnd = Math.min(end, pageStart + PAGE_SIZE)

        const buffer = new Uint8Array(PAGE_SIZE)
        const prior = existing.get(page)
        // Whatever a truncation left in the page past the end is not the
        // file's; the file reads as zeroes there, as it would on disk.
        if (prior) buffer.set(prior.subarray(0, Math.max(0, Math.min(PAGE_SIZE, this.length - pageStart))))
        buffer.set(
          data.subarray(writeStart - req.offset, writeEnd - req.offset),
          writeStart - pageStart
        )
        ops.push(['put', this._pageKey(page), buffer])
      }
      if (length !== this.length) ops.push(['put', this._metaKey, { size: length }])
      await this.db.write(ops)
      this.length = length
      req.callback(null)
    } catch (err) {
      req.callback(err)
    }
  }

  async _read (req) {
    try {
      if (req.offset + req.size > this.length) {
        // Hypercore probes for headers on files that may not exist yet and
        // relies on this failing rather than returning zeroes.
        const error = new Error('Could not satisfy length')
        error.code = 'ENOENT'
        return req.callback(error)
      }
      if (req.size === 0) return req.callback(null, b4a.alloc(0))

      const firstPage = Math.floor(req.offset / PAGE_SIZE)
      const lastPage = Math.floor((req.offset + req.size - 1) / PAGE_SIZE)

      const wanted = []
      for (let page = firstPage; page <= lastPage; page++) wanted.push(page)
      const pages = await this._pages(wanted)

      const out = b4a.alloc(req.size)
      for (let page = firstPage; page <= lastPage; page++) {
        const raw = pages.get(page)
        if (!raw) continue // never-written page inside the file reads as zeroes
        const pageStart = page * PAGE_SIZE
        const readStart = Math.max(req.offset, pageStart)
        const readEnd = Math.min(req.offset + req.size, pageStart + PAGE_SIZE)
        out.set(
          new Uint8Array(raw).subarray(readStart - pageStart, readEnd - pageStart),
          readStart - req.offset
        )
      }

      req.callback(null, out)
    } catch (err) {
      req.callback(err)
    }
  }

  _del (req) {
    this._serial(() => this._delSerial(req))
  }

  async _delSerial (req) {
    try {
      if (req.offset >= this.length) return req.callback(null)
      const end = req.size === Infinity ? this.length : Math.min(this.length, req.offset + req.size)
      const length = end >= this.length ? req.offset : this.length

      // Only whole pages can be dropped; a partial range keeps its page and
      // simply stops being reachable once the length shrinks.
      const ops = []
      const firstWhole = Math.ceil(req.offset / PAGE_SIZE)
      const lastWhole = Math.floor(end / PAGE_SIZE) - 1
      for (let page = firstWhole; page <= lastWhole; page++) {
        ops.push(['del', this._pageKey(page)])
      }
      if (length !== this.length) ops.push(['put', this._metaKey, { size: length }])
      if (ops.length) await this.db.write(ops)
      this.length = length
      req.callback(null)
    } catch (err) {
      req.callback(err)
    }
  }

  _truncate (req) {
    this._serial(() => this._truncateSerial(req))
  }

  async _truncateSerial (req) {
    try {
      const ops = []
      const from = Math.ceil(req.offset / PAGE_SIZE)
      const to = Math.ceil(this.length / PAGE_SIZE)
      for (let page = from; page < to; page++) ops.push(['del', this._pageKey(page)])
      ops.push(['put', this._metaKey, { size: req.offset }])
      await this.db.write(ops)
      this.length = req.offset
      req.callback(null)
    } catch (err) {
      req.callback(err)
    }
  }

  _stat (req) {
    req.callback(null, { size: this.length })
  }

  _close (req) {
    // Let queued writes finish so closing cannot strand a half-written page.
    this._serial(() => req.callback(null))
  }

  _destroy (req) {
    this._serial(() => this._destroySerial(req))
  }

  async _destroySerial (req) {
    try {
      const ops = []
      const pages = Math.ceil(this.length / PAGE_SIZE)
      for (let page = 0; page < pages; page++) ops.push(['del', this._pageKey(page)])
      ops.push(['del', this._metaKey])
      await this.db.write(ops)
      this.length = 0
      req.callback(null)
    } catch (err) {
      req.callback(err)
    }
  }

  async _setLength (size) {
    await this.db.write([['put', this._metaKey, { size }]])
    this.length = size
  }

  async _get (key) {
    const [value] = await this.db.get([key])
    return value
  }

  /** These pages of this file, from memory where possible: page -> bytes. */
  async _pages (numbers) {
    const found = new Map()
    const missing = []
    for (const page of numbers) {
      const held = this.db.cached(this._pageKey(page))
      if (held) found.set(page, held)
      else missing.push(page)
    }
    if (missing.length) {
      const values = await this.db.get(missing.map((page) => this._pageKey(page)))
      missing.forEach((page, i) => {
        if (!values[i]) return // never written: reads as zeroes
        const bytes = new Uint8Array(values[i])
        this.db.remember(this._pageKey(page), bytes)
        found.set(page, bytes)
      })
    }
    return found
  }
}

function promisify (request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function deferred () {
  let resolve, reject
  const promise = new Promise((_resolve, _reject) => { resolve = _resolve; reject = _reject })
  promise.catch(noop) // every caller awaits it; this only keeps a failure from being reported twice
  return { promise, resolve, reject }
}

function settled (tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error || abortError())
  })
}

function abortError () {
  const error = new Error('IndexedDB transaction aborted')
  error.name = 'AbortError'
  return error
}

function sleep (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function noop () {}

/** Factory in the shape Corestore expects: (fileName) => RandomAccessStorage. */
export function idbStorage (dbName = 'pine') {
  const db = new Database(dbName)
  return (file) => new IdbFile(db, file)
}

export async function estimateUsage () {
  if (!navigator.storage || !navigator.storage.estimate) return null
  const { usage, quota } = await navigator.storage.estimate()
  return { usage, quota }
}

/** Ask the browser not to evict us under storage pressure. */
export async function requestPersistence () {
  if (!navigator.storage || !navigator.storage.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}

export default idbStorage
